import type { ChangeRequest as WireChangeRequest, LocalizedText } from '@noor/eats-api';
import {
  CHANGE_REQUEST_ENTITY_KINDS,
  CHANGE_REQUEST_OPERATIONS,
  CHANGE_REQUEST_STATUSES,
  type ChangeRequest,
  type ChangeRequestEntityKind,
  type ChangeRequestOperation,
  type ChangeRequestStatus,
} from '../model/schema';

// Wire (`v1ChangeRequest`) → the «Заявки» screen's view model. Pure and
// unit-tested, so protobuf-flavoured payloads never reach a widget.

/**
 * Narrow a free-form wire string to one of the modelled members, degrading to
 * `unknown` rather than throwing.
 *
 * The three enums are all open strings on the wire (see
 * `@noor/eats-api`'s `model/moderation.ts`), and a novel value must cost one
 * vaguely-labelled row rather than the whole queue.
 */
function narrow<T extends string>(
  members: readonly T[],
  value: string | undefined,
): T | 'unknown' {
  return members.includes(value as T) ? (value as T) : 'unknown';
}

/**
 * A localized name resolved to one display string, preferring `ru` — the
 * backend's own fallback for an untranslated locale, and what every other
 * mapper here does (`toMenuCategoryFromLocalized`).
 *
 * An all-blank `LocalizedText` yields `undefined`, not `''`: the row then falls
 * back to its type label instead of rendering an empty cell.
 */
function toDisplayName(text: LocalizedText | undefined): string | undefined {
  if (text === undefined) return undefined;
  const name = text.ru || text.uz || text.en;
  return name === '' ? undefined : name;
}

/** One queue row. */
export function toChangeRequest(request: WireChangeRequest): ChangeRequest {
  return {
    id: request.id,
    entityKind: narrow<ChangeRequestEntityKind>(
      CHANGE_REQUEST_ENTITY_KINDS,
      request.entity_type,
    ),
    ...(request.entity_id === undefined ? {} : { entityId: request.entity_id }),
    ...(toDisplayName(request.entity_name) === undefined
      ? {}
      : { entityName: toDisplayName(request.entity_name) }),
    operation: narrow<ChangeRequestOperation>(
      CHANGE_REQUEST_OPERATIONS,
      request.operation,
    ),
    status: narrow<ChangeRequestStatus>(CHANGE_REQUEST_STATUSES, request.status),
    // Only ever meaningful on a rejection, and the wire sends `''` for "no
    // reason given" — collapsed so the row can test presence rather than
    // rendering an empty quote.
    ...(request.rejection_reason ? { rejectionReason: request.rejection_reason } : {}),
    ...(request.source ? { source: request.source } : {}),
    ...(request.created_at === undefined ? {} : { createdAt: request.created_at }),
    ...(request.reviewed_at === undefined ? {} : { reviewedAt: request.reviewed_at }),
  };
}

/**
 * Whether a request can still be withdrawn.
 *
 * Only a `pending` one: every other state is settled, and `POST .../cancel`
 * rejects them. The card reads this rather than testing the status inline, so
 * the rule lives in one place alongside the queue's other logic.
 */
export function isWithdrawable(request: ChangeRequest): boolean {
  return request.status === 'pending';
}
